import { useCallback, useMemo, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  CenteredPanel,
  ConfirmationDialog,
  Container,
  Content,
  Footer,
  Frame,
  Heading,
  Icon,
  InPlaceInput,
  Menu,
  MenuItem,
  Stack,
  Text,
  Toolbar,
  ToolbarItem,
  useToast,
} from '@kinesis/bungle';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useScenarioId } from '@/hooks/use-scenario-id';
import { useIsEditor } from '@/hooks/use-is-editor';
import {
  useCreateScenarioUploadMutation,
  useDeclareScopeRuleMutation,
  useDeleteScopeRuleMutation,
  useDownloadScenarioMethodologyMutation,
  useGetScenarioQuery,
  useGetScopeRulesQuery,
  useRemoveScenarioMethodologyMutation,
  useUpdateScenarioMutation,
  useSetScenarioMethodologyMutation,
} from '@/api/scenarios';
import {
  useGetScenarioEfLibrariesQuery,
  useGetEfLibraryCatalogQuery,
  useInstallEfLibraryMutation,
  useUninstallEfLibraryMutation,
} from '@/api/ef-libraries';
import { Loading } from '@/components/loading';
import { Layout } from '@/components/layout';
import { TextBlock } from '@/components/text-block';
import { InventoryScopeControl } from '@/components/inventory-scope-control';
import { MethodologyReference } from '@/components/methodology-reference';
import { ScenarioEmissionFactorLibraries } from '@/components/scenario-emission-factor-libraries';
import { ScenarioHorizonSelect } from '@/components/scenario-horizon-select';
import { ScenarioReportingPeriodControl } from '@/components/scenario-reporting-period-control';
import { scopeRuleKey } from '@/data/scope-rules';
import type {
  Scenario as ScenarioModel,
  ScopeRuleDraft,
  InventoryScopeRuleEntry,
} from '@/types';

type Article =
  | 'scenario-scope'
  | 'scenario-horizon'
  | 'scenario-reporting-period'
  | 'scenario-emission-factors'
  | 'scenario-methodology';

type ScenarioLoadedProps = {
  scenario: ScenarioModel;
  entries: InventoryScopeRuleEntry[];
};

// The community EF-scenario editor. Same shape as suit's `ScenarioLoaded`: a
// left section menu, a centred titled panel showing the active section, and a
// dirty save/discard footer (+ a navigation blocker). The scenario is the
// ledger-build input a disclosure pins — its name, its scope rules, and its
// emission factor libraries.
//
// Name + scope edits are held locally and applied on Save (village's scope-rule
// API is granular — idempotent declare + delete-by-id — so Save reconciles the
// local rules against the saved set). Emission factor library edits (attach /
// remove / pin / reorder) fire immediately, independent of the dirty footer,
// matching suit.
const ScenarioLoaded = (props: ScenarioLoadedProps) => {
  const { scenario, entries } = props;
  const scenarioId = scenario.id;
  const isEditor = useIsEditor();
  const toast = useToast('globalTop');

  const { data: libraries } = useGetScenarioEfLibrariesQuery(scenarioId);
  const { data: catalog } = useGetEfLibraryCatalogQuery(scenario.inventory);
  const [installEfLibrary] = useInstallEfLibraryMutation();
  const [uninstallEfLibrary] = useUninstallEfLibraryMutation();
  const efLibraries = useMemo(() => libraries ?? [], [libraries]);

  const [updateScenario] = useUpdateScenarioMutation();
  const [declareScopeRule] = useDeclareScopeRuleMutation();
  const [deleteScopeRule] = useDeleteScopeRuleMutation();

  // Attach at lowest precedence, following latest — the user adjusts both after.
  const handleAttachLibrary = useCallback(
    (library: number) => {
      installEfLibrary({ scenarioId, library, version: null, precedence: 0 });
    },
    [installEfLibrary, scenarioId],
  );

  const handleRemoveLibrary = useCallback(
    (library: number) => {
      uninstallEfLibrary({ scenarioId, library });
    },
    [uninstallEfLibrary, scenarioId],
  );

  // Pin freezes the binding at the library's latest published version; unpin
  // returns it to following latest. Precedence is preserved (re-install upserts).
  const handlePinLibrary = useCallback(
    (library: number, pinned: boolean) => {
      const entry = efLibraries.find((l) => l.efLibrary === library);
      if (!entry) return;
      installEfLibrary({
        scenarioId,
        library,
        version: pinned ? entry.latestVersion : null,
        precedence: entry.precedence,
      });
    },
    [efLibraries, installEfLibrary, scenarioId],
  );

  // The list renders highest-precedence-first; map the dropped order back to
  // descending precedence and re-install each changed binding (pin preserved).
  const handleReorderLibraries = useCallback(
    (orderedLibraryIds: number[]) => {
      const count = orderedLibraryIds.length;
      orderedLibraryIds.forEach((library, index) => {
        const entry = efLibraries.find((l) => l.efLibrary === library);
        if (!entry) return;
        const precedence = count - 1 - index;
        if (precedence !== entry.precedence) {
          installEfLibrary({
            scenarioId,
            library,
            version: entry.version,
            precedence,
          });
        }
      });
    },
    [efLibraries, installEfLibrary, scenarioId],
  );

  // The methodology reference: upload runs the standard transfer flow
  // (mint upload -> PUT the bytes to the presigned URL -> bind the key);
  // set/remove fire immediately, independent of the dirty footer, like the
  // EF-library edits. The drag-and-drop path bypasses the browse dialog's
  // extension filter, so non-PDFs are re-checked here (village would 400
  // them anyway).
  const [createScenarioUpload] = useCreateScenarioUploadMutation();
  const [setScenarioMethodology] = useSetScenarioMethodologyMutation();
  const [removeScenarioMethodology] = useRemoveScenarioMethodologyMutation();
  const [downloadScenarioMethodology, { isLoading: methodologyDownloading }] =
    useDownloadScenarioMethodologyMutation();
  const [methodologyBusy, setMethodologyBusy] = useState(false);

  const handleUploadMethodology = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast('The methodology reference must be a PDF.', {
          variant: 'error',
        });
        return;
      }
      setMethodologyBusy(true);
      try {
        const { key, url } = await createScenarioUpload({
          scenarioId,
          format: 'pdf',
        }).unwrap();
        await axios.put(url, file, {
          headers: {
            'Content-Type': file.type || 'application/pdf',
            'x-amz-server-side-encryption': 'AES256',
          },
        });
        await setScenarioMethodology({
          scenarioId,
          key,
          filename: file.name,
          format: 'pdf',
        }).unwrap();
        toast('Methodology reference uploaded.', { variant: 'success' });
      } catch {
        toast(
          'We couldn’t upload the methodology reference. Please try again.',
          { variant: 'error' },
        );
      } finally {
        setMethodologyBusy(false);
      }
    },
    [createScenarioUpload, scenarioId, setScenarioMethodology, toast],
  );

  const handleRemoveMethodology = useCallback(async () => {
    setMethodologyBusy(true);
    try {
      await removeScenarioMethodology({ scenarioId }).unwrap();
    } catch {
      toast('We couldn’t remove the methodology reference. Please try again.', {
        variant: 'error',
      });
    } finally {
      setMethodologyBusy(false);
    }
  }, [removeScenarioMethodology, scenarioId, toast]);

  const handleDownloadMethodology = useCallback(() => {
    downloadScenarioMethodology({ scenarioId });
  }, [downloadScenarioMethodology, scenarioId]);

  const [activeArticle, setActiveArticle] = useState<Article>('scenario-scope');
  const [localName, setLocalName] = useState(scenario.name);
  const [localHorizonYear, setLocalHorizonYear] = useState(
    scenario.horizonYear,
  );
  const [localReportingEndMonth, setLocalReportingEndMonth] = useState(
    scenario.reportingEndMonth,
  );
  const [localLastReviewedYear, setLocalLastReviewedYear] = useState(
    scenario.lastReviewedYear,
  );
  const [localRules, setLocalRules] = useState<ScopeRuleDraft[]>(() =>
    entries.map((e) => ({ id: e.id, specifier: e.specifier })),
  );
  const [hasChanges, setHasChanges] = useState(false);

  const handleNameChange = useCallback((name: string) => {
    setLocalName(name);
    setHasChanges(true);
  }, []);

  const handleHorizonChange = useCallback((value: number | null) => {
    setLocalHorizonYear(value);
    setHasChanges(true);
  }, []);

  const handleReportingEndMonthChange = useCallback((value: number | null) => {
    setLocalReportingEndMonth(value);
    setHasChanges(true);
  }, []);

  const handleLastReviewedYearChange = useCallback((value: number | null) => {
    setLocalLastReviewedYear(value);
    setHasChanges(true);
  }, []);

  const handleScopeChange = useCallback((next: ScopeRuleDraft[]) => {
    setLocalRules(next);
    setHasChanges(true);
  }, []);

  const onDiscard = useCallback(() => {
    setLocalName(scenario.name);
    setLocalHorizonYear(scenario.horizonYear);
    setLocalReportingEndMonth(scenario.reportingEndMonth);
    setLocalLastReviewedYear(scenario.lastReviewedYear);
    setLocalRules(entries.map((e) => ({ id: e.id, specifier: e.specifier })));
    setHasChanges(false);
  }, [
    scenario.name,
    scenario.horizonYear,
    scenario.reportingEndMonth,
    scenario.lastReviewedYear,
    entries,
  ]);

  const onSave = useCallback(async () => {
    try {
      // Name + horizon + reporting period are the scenario's scalar fields —
      // one atomic update carrying every field (village clears omitted
      // ones), not separate writes racing on the row.
      if (
        localName !== scenario.name ||
        localHorizonYear !== scenario.horizonYear ||
        localReportingEndMonth !== scenario.reportingEndMonth ||
        localLastReviewedYear !== scenario.lastReviewedYear
      ) {
        await updateScenario({
          scenarioId,
          name: localName,
          horizonYear: localHorizonYear,
          reportingEndMonth: localReportingEndMonth,
          lastReviewedYear: localLastReviewedYear,
        }).unwrap();
      }
      // Reconcile the local rules against the saved set by axis+value key.
      const savedKeys = new Set(entries.map((e) => scopeRuleKey(e.specifier)));
      const localKeys = new Set(
        localRules.map((r) => scopeRuleKey(r.specifier)),
      );
      const toDeclare = localRules.filter(
        (r) => !savedKeys.has(scopeRuleKey(r.specifier)),
      );
      const toDelete = entries.filter(
        (e) => !localKeys.has(scopeRuleKey(e.specifier)),
      );
      await Promise.all([
        ...toDeclare.map((r) =>
          declareScopeRule({ scenarioId, specifier: r.specifier }).unwrap(),
        ),
        ...toDelete.map((e) => deleteScopeRule({ ruleId: e.id }).unwrap()),
      ]);
      setHasChanges(false);
      toast('Scenario saved.', { variant: 'success' });
    } catch {
      toast('We couldn’t save the scenario. Please try again.', {
        variant: 'error',
      });
    }
  }, [
    localName,
    localHorizonYear,
    localReportingEndMonth,
    localLastReviewedYear,
    scenario.name,
    scenario.horizonYear,
    scenario.reportingEndMonth,
    scenario.lastReviewedYear,
    updateScenario,
    scenarioId,
    entries,
    localRules,
    declareScopeRule,
    deleteScopeRule,
    toast,
  ]);

  // Guard navigation away from unsaved edits (suit parity).
  const blocker = useBlocker(hasChanges && isEditor);
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (hasChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    [hasChanges],
  );
  useBeforeUnload(handleBeforeUnload);
  const handleDiscardChanges = useCallback(() => {
    onDiscard();
    if (blocker.proceed) blocker.proceed();
  }, [blocker, onDiscard]);
  const handleSaveChanges = useCallback(async () => {
    await onSave();
    if (blocker.proceed) blocker.proceed();
  }, [blocker, onSave]);

  const menuVariant = useCallback(
    (article: Article) => (activeArticle === article ? 'accent' : 'default'),
    [activeArticle],
  );

  return (
    <Container>
      <Frame sizing='fit-content' borderRight>
        <Content
          background='white'
          height='100%'
          padding='medium'
          sizing='fill-container'
          width={239}
        >
          <Stack space='medium'>
            <Heading as='h4' size='small'>
              Scenario
            </Heading>
            <Menu>
              <MenuItem
                content='Scenario scope'
                onClick={() => setActiveArticle('scenario-scope')}
                variant={menuVariant('scenario-scope')}
              />
              <MenuItem
                content='Horizon'
                onClick={() => setActiveArticle('scenario-horizon')}
                variant={menuVariant('scenario-horizon')}
              />
              <MenuItem
                content='Reporting period'
                onClick={() => setActiveArticle('scenario-reporting-period')}
                variant={menuVariant('scenario-reporting-period')}
              />
              <MenuItem
                content='Emission factors'
                onClick={() => setActiveArticle('scenario-emission-factors')}
                variant={menuVariant('scenario-emission-factors')}
              />
              <MenuItem
                content='Methodology reference'
                onClick={() => setActiveArticle('scenario-methodology')}
                variant={menuVariant('scenario-methodology')}
              />
            </Menu>
          </Stack>
        </Content>
      </Frame>
      <Frame>
        <Container>
          <Frame>
            <CenteredPanel minWidth={482} maxWidth={976}>
              <Content padding='medium'>
                <Content width='100%'>
                  <Stack space='medium'>
                    <InPlaceInput
                      onChange={handleNameChange}
                      placeholder='Scenario name'
                      value={localName}
                      variant='title'
                      disabled={!isEditor}
                    />
                    {activeArticle === 'scenario-scope' && (
                      <Stack space='medium'>
                        <TextBlock>
                          Scenario scope trims which activity is included in
                          this scenario&rsquo;s totals. By default the scenario
                          covers the whole inventory; each rule below removes
                          the activity it matches.
                        </TextBlock>
                        <Heading as='h4' size='small'>
                          Inventory scope
                        </Heading>
                        <Content
                          background='white'
                          border
                          padding='medium'
                          paddingMode='equal'
                        >
                          <InventoryScopeControl
                            rules={localRules}
                            disabled={!isEditor}
                            onChange={handleScopeChange}
                          />
                        </Content>
                      </Stack>
                    )}
                    {activeArticle === 'scenario-horizon' && (
                      <Stack space='medium'>
                        <TextBlock>
                          The horizon sets the earliest reporting year included
                          in this scenario &mdash; the whole year, so a
                          July&ndash;June scenario starting at 2024 includes
                          from July 2023. Activity before it is left out of the
                          scenario&rsquo;s ledger entirely, so it never appears
                          in any report or chart built from this scenario. Leave
                          it at &lsquo;earliest data point&rsquo; to include all
                          activity.
                        </TextBlock>
                        <Heading as='h4' size='small'>
                          Horizon
                        </Heading>
                        <Content
                          background='white'
                          border
                          padding='medium'
                          paddingMode='equal'
                        >
                          <ScenarioHorizonSelect
                            value={localHorizonYear}
                            onChange={handleHorizonChange}
                            disabled={!isEditor}
                          />
                        </Content>
                      </Stack>
                    )}
                    {activeArticle === 'scenario-reporting-period' && (
                      <Stack space='medium'>
                        <TextBlock>
                          The reporting period sets how this scenario&rsquo;s
                          years are presented: the month the reporting year ends
                          (for example June for a July&ndash;June year), and the
                          last year whose data has been reviewed. Years up to
                          the last reviewed year chart as complete; anything
                          later appears provisional.
                        </TextBlock>
                        <Heading as='h4' size='small'>
                          Reporting period
                        </Heading>
                        <Content
                          background='white'
                          border
                          padding='medium'
                          paddingMode='equal'
                        >
                          <ScenarioReportingPeriodControl
                            endMonth={localReportingEndMonth}
                            lastReviewedYear={localLastReviewedYear}
                            onEndMonthChange={handleReportingEndMonthChange}
                            onLastReviewedYearChange={
                              handleLastReviewedYearChange
                            }
                            disabled={!isEditor}
                          />
                        </Content>
                      </Stack>
                    )}
                    {activeArticle === 'scenario-emission-factors' && (
                      <Stack space='medium'>
                        <TextBlock>
                          Emission factor libraries drive how activity data is
                          converted into emissions. Attach the libraries this
                          scenario should use, choose whether each follows its
                          latest published version or is pinned, and order them
                          — higher entries win where libraries overlap.
                        </TextBlock>
                        <Heading as='h4' size='small'>
                          Emission factor libraries
                        </Heading>
                        <ScenarioEmissionFactorLibraries
                          libraries={efLibraries}
                          catalog={isEditor ? catalog : undefined}
                          onReorder={
                            isEditor ? handleReorderLibraries : undefined
                          }
                          onPinChange={isEditor ? handlePinLibrary : undefined}
                          onAttach={isEditor ? handleAttachLibrary : undefined}
                          onRemove={isEditor ? handleRemoveLibrary : undefined}
                        />
                      </Stack>
                    )}
                    {activeArticle === 'scenario-methodology' && (
                      <Stack space='medium'>
                        <TextBlock>
                          The methodology reference is the document that
                          justifies this scenario&rsquo;s choices — its emission
                          factors and scope. A single PDF, replaced in place; it
                          also appears on the methodology page of any report
                          built from this scenario.
                        </TextBlock>
                        <Heading as='h4' size='small'>
                          Methodology reference
                        </Heading>
                        <MethodologyReference
                          methodology={scenario.methodology}
                          busy={methodologyBusy}
                          downloading={methodologyDownloading}
                          onUpload={
                            isEditor ? handleUploadMethodology : undefined
                          }
                          onRemove={
                            isEditor ? handleRemoveMethodology : undefined
                          }
                          onDownload={handleDownloadMethodology}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Content>
              </Content>
            </CenteredPanel>
            {blocker.state === 'blocked' && (
              <ConfirmationDialog
                confirmText='Save changes'
                onCancel={blocker.reset}
                onConfirm={handleSaveChanges}
                onSecondary={handleDiscardChanges}
                secondaryText='Discard changes'
                showSecondaryButton
                title={`Save changes to “${localName}”?`}
                variant='primary'
              >
                Your changes to this scenario will be visible to others.
              </ConfirmationDialog>
            )}
          </Frame>
          {hasChanges && isEditor && (
            <Footer border shadow>
              <Toolbar>
                <ToolbarItem>
                  <Icon type='info-circle' />
                </ToolbarItem>
                <ToolbarItem ellipsify>
                  <Text>Save your changes to make them visible to others.</Text>
                </ToolbarItem>
                <ToolbarItem>
                  <Button variant='secondary' onClick={onDiscard}>
                    Discard changes
                  </Button>
                </ToolbarItem>
                <ToolbarItem>
                  <Button onClick={onSave}>Save changes</Button>
                </ToolbarItem>
              </Toolbar>
            </Footer>
          )}
          {hasChanges && !isEditor && (
            <Footer border shadow>
              <Toolbar>
                <ToolbarItem>
                  <Icon type='exclamation-circle' />
                </ToolbarItem>
                <ToolbarItem ellipsify>
                  <Text>
                    You have view-only access and cannot save changes.
                  </Text>
                </ToolbarItem>
                <ToolbarItem>
                  <Button variant='secondary' onClick={onDiscard}>
                    Discard changes
                  </Button>
                </ToolbarItem>
              </Toolbar>
            </Footer>
          )}
        </Container>
      </Frame>
    </Container>
  );
};

// Loading wrapper: fetch the scenario + its scope rules, then hand them to
// the editor as initial state (mirrors suit's `scenario.loading`).
const Scenario = () => {
  useInventoryId();
  const scenarioId = useScenarioId();
  const { data: scenario, isSuccess: scenarioReady } =
    useGetScenarioQuery(scenarioId);
  const { data: entries, isSuccess: entriesReady } =
    useGetScopeRulesQuery(scenarioId);

  if (!scenarioReady || !entriesReady || !scenario) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  // Key on the scenario id so local edit state resets when navigating between
  // scenarios.
  return (
    <ScenarioLoaded
      key={scenario.id}
      scenario={scenario}
      entries={entries ?? []}
    />
  );
};

export { Scenario };
